import { baseInstallerUrl, installerUrl } from '../constants/constants';
/**
 * Generates promise object that can be resolved or rejected via functions
 *
 * @returns an object containing the promise, the resolver and rejecter
 */
export var generatePromiseObjects = function () {
    var resolver;
    var rejecter;
    var promise = new Promise(function (resolve, reject) {
        resolver = resolve;
        rejecter = reject;
    });
    return {
        promise: promise,
        resolver: resolver,
        rejecter: rejecter
    };
};
/**
 * Log errors from Aspera SDK
 *
 * @param message the message indicating the error encountered
 * @param debugData the data with useful debugging information
 */
export var errorLog = function (message, debugData) {
    if (debugData && debugData.code && debugData.message) {
        debugData = {
            code: debugData.code,
            message: debugData.message,
            data: debugData.data
        };
    }
    if (typeof window === 'object') {
        if (!Array.isArray(window.asperaSdkLogs)) {
            window.asperaSdkLogs = [];
        }
        window.asperaSdkLogs.push({ message: message, debugData: debugData });
    }
    console.warn("Aspera SDK: ".concat(message), debugData);
};
/**
 * Generate error object for rejecter responses
 *
 * @param message the message indicating the error encountered
 * @param debugData the data with useful debugging information
 *
 * @returns object containing standardized error response
 */
export var generateErrorBody = function (message, debugData) {
    var errorResponse = {
        error: true,
        message: message
    };
    if (debugData && debugData.code && debugData.message) {
        errorResponse.debugData = {
            code: debugData.code,
            message: debugData.message,
            data: debugData.data
        };
    }
    return errorResponse;
};
/**
 * Validate if transferSpec is valid for server communication
 *
 * @param transferSpec the transferSpec to test
 *
 * @returns boolean indicating whether supplied transferSpec is valid
 */
export var isValidTransferSpec = function (transferSpec) {
    if (transferSpec &&
        typeof transferSpec === 'object' &&
        typeof transferSpec.direction === 'string' &&
        typeof transferSpec.remote_host === 'string' &&
        Array.isArray(transferSpec.paths)) {
        return true;
    }
    return false;
};
/**
 * Returns a string indicating the websocket URL to use for talking to the server
 *
 * @returns a string of the full Websocket URL
 */
export var getWebsocketUrl = function (serverUrl) {
    var wsProtocol;
    if (serverUrl.indexOf('http:') === 0) {
        wsProtocol = 'ws';
    }
    else if (serverUrl.indexOf('https:') === 0) {
        wsProtocol = 'wss';
    }
    var url = serverUrl.replace('http://', '//').replace('https://', '//');
    return "".concat(wsProtocol, ":").concat(url);
};
/**
 * Simple function to get the current platform.
 *
 * @returns a string indicating the current platform
 */
export var getCurrentPlatform = function () {
    var ua = navigator.userAgent;
    if (/Mac/.test(ua)) {
        return 'macos';
    }
    else if (/Win/.test(ua)) {
        return 'windows';
    }
    else if (/Linux/.test(ua)) {
        return 'linux';
    }
    else {
        return 'unknown';
    }
};
/**
 * Function used to create a random UUID
 *
 * @returns string
 */
export var randomUUID = function () {
    var _a;
    var fallback = function () {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Number(((new Date().getTime() + 16) * Math.random()).toFixed()) % 16;
            if (c !== 'x') {
                // eslint-disable-next-line no-bitwise
                r = r & 0x3 | 0x8;
            }
            return r.toString(16);
        });
    };
    return ((_a = window.crypto) === null || _a === void 0 ? void 0 : _a.randomUUID) ? window.crypto.randomUUID() : fallback();
};
/**
 * Return a rejected promise
 *
 * @param message the message indicating the error encountered
 * @param debugData the data with useful debugging information
 *
 * @returns a rejected promise
 */
export var throwError = function (message, debugData) {
    errorLog(message, debugData);
    return new Promise(function (resolve, reject) {
        reject(generateErrorBody(message, debugData));
    });
};
/**
 * Check if the given string is a valid URL
 *
 * @param url string to check if valid URL
 *
 * @returns boolean
 */
export var isValidURL = function (url) {
    try {
        new URL(url);
        return true;
    }
    catch (error) {
        return false;
    }
};
/**
 * Checks if the current browser is Safari.
 * @returns {boolean} Whether the browser is Safari.
 */
export var isSafari = function () {
    // eslint-disable-next-line
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && !window.MSStream;
};
/**
 * Get the URLs for installer management.
 *
 * @returns Info on URLs where installers live
 */
export var getInstallerUrls = function () {
    return {
        base: baseInstallerUrl,
        latest: installerUrl,
    };
};
export default {
    errorLog: errorLog,
    generateErrorBody: generateErrorBody,
    generatePromiseObjects: generatePromiseObjects,
    getCurrentPlatform: getCurrentPlatform,
    getWebsocketUrl: getWebsocketUrl,
    isSafari: isSafari,
    isValidURL: isValidURL,
    isValidTransferSpec: isValidTransferSpec,
    randomUUID: randomUUID,
    throwError: throwError,
    getInstallerUrls: getInstallerUrls,
};
